/**
 * Markdown rendering configuration constants
 * Defines allowed HTML tags, code language mappings, and theme-aware styles
 */

import type { SxProps, Theme } from "@mui/material";

/**
 * Allowed HTML tags for XSS prevention
 * Only these tags will be rendered from markdown, others are stripped
 */
export const ALLOWED_HTML_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "u",
  "s",
  "code",
  "pre",
  "a",
  "ul",
  "ol",
  "li",
  "blockquote",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
  "img",
  "span",
  "div",
] as const;

/**
 * Code language aliases mapping
 * Maps common aliases to their canonical language names
 */
export const CODE_LANGUAGE_MAP: Record<string, string> = {
  js: "javascript",
  ts: "typescript",
  tsx: "typescript",
  jsx: "javascript",
  py: "python",
  sh: "bash",
  shell: "bash",
  yml: "yaml",
  md: "markdown",
  rb: "ruby",
  rs: "rust",
  kt: "kotlin",
  cs: "csharp",
} as const;

/**
 * Regex pattern for matching reference links in markdown
 * Matches patterns like #ref-1, #ref-2, etc.
 */
export const REFERENCE_LINK_PATTERN = /#ref-(\d+)/;

/**
 * Theme-aware markdown styles
 * All styles use MUI theme values for consistency
 */
export const MARKDOWN_STYLES = {
  container: {
    fontFamily: "Plus Jakarta Sans Variable, sans-serif",
    fontSize: "16px",
    lineHeight: "30px",
    fontWeight: "500",
    color: "text.primary",
  } as SxProps<Theme>,

  paragraph: {
    marginBottom: 2,
    fontSize: "16px",
    lineHeight: "30px",
  } as SxProps<Theme>,

  heading: {
    marginTop: 3,
    marginBottom: 2,
    fontWeight: 600,
  } as SxProps<Theme>,

  list: {
    paddingLeft: 3,
    marginBottom: 2,
    listStylePosition: "outside",
  } as SxProps<Theme>,

  orderedList: {
    paddingLeft: 3,
    marginBottom: 2,
    listStylePosition: "outside",
    listStyle: "decimal",
    "& ol": {
      listStyle: "lower-alpha",
      marginTop: 1,
    },
    "& ol ol": {
      listStyle: "lower-roman",
    },
  } as SxProps<Theme>,

  unorderedList: {
    paddingLeft: 3,
    marginBottom: 2,
    listStylePosition: "outside",
    listStyle: "disc",
    "& ul": {
      listStyle: "circle",
      marginTop: 1,
    },
    "& ul ul": {
      listStyle: "square",
    },
  } as SxProps<Theme>,

  listItem: {
    display: "list-item",
    marginBottom: 0.5,
    fontSize: "16px",
    lineHeight: "30px",
  } as SxProps<Theme>,

  blockquote: {
    borderLeft: 4,
    borderColor: "primary.main",
    paddingLeft: 2,
    marginY: 2,
    color: "text.secondary",
    fontStyle: "italic",
  } as SxProps<Theme>,

  inlineCode: {
    fontFamily: "monospace",
    backgroundColor: "grey.100",
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "0.9em",
    color: "text.primary",
  } as SxProps<Theme>,

  codeBlock: {
    backgroundColor: "grey.50",
    borderRadius: "8px",
    padding: 2,
    marginY: 2,
    overflow: "auto",
  } as SxProps<Theme>,

  codeBlockHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 1,
    paddingX: 1,
  } as SxProps<Theme>,

  referenceLink: {
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    width: "13px",
    height: "13px",
    color: "primary.dark",
    backgroundColor: "primary.light",
    borderRadius: "30%",
    fontSize: "75%",
    lineHeight: 1,
    verticalAlign: "super",
    marginLeft: "2px",
    marginRight: "2px",
    textDecoration: "none",
    cursor: "pointer",
  } as SxProps<Theme>,

  table: {
    width: "100%",
    marginY: 2,
    borderCollapse: "collapse",
  } as SxProps<Theme>,

  tableCell: {
    border: 1,
    borderColor: "divider",
    padding: 1,
  } as SxProps<Theme>,
} as const;
